import React from "react";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import ReplayIcon from "@mui/icons-material/Replay";

import PaperFragment from "../../../../helper-components/misc/PaperFragment";
import StackedHeader from "../../../../helper-components/misc/StackedHeader";
import DataGridTable from "../../../../helper-components/table/DataGridTable";

import AudioTranslationEditForm from "./AudioTranslationEditForm";
import AudioTranslationAddStepper from "./AudioTranslationAddStepper";

import { columns, defaultTableOptions } from "../../../../helper-functions/table/audioTranslations";

import { createUploadObject } from "../../../../helper-functions/shared/miscUtils";
import {
    approveAudioTranslations,
    createAudioTranslation,
    deleteAudioTranslations,
    queryAudioTranslations,
    retryAudioTranslations,
    updateAudioTranslation
} from "../../../../helper-functions/api/translationQueries";
import { getLanguage } from "../../../../config/language/language";

/**
 * Table showing audio translations.
 * If no data found, show error message instead.
 */
export default function AudioTranslationTable(props) {
    const audioTranslations = props.backend.audioTranslations;

    const showCreateDialog = () => {
        props.showDialog({
            title: "Add new audio translation",
            component: <AudioTranslationAddStepper {...props} callback={(confirm, options) => handleCreate(confirm, options)} />
        });
    };

    const showDeleteDialog = () => {
        props.showDialog({
            title: "Delete audio translation(s)",
            message: `Are you sure you want to delete the ${audioTranslations.selected.length} selected audio translation(s)?`,
            actionTitle: "Delete",
            actionColor: "error",
            callback: (confirm) => handleDelete(confirm)
        });
    };

    const showUpdateDialog = () => {
        props.showDialog({
            title: "Edit audio translation",
            message: "Click 'Update' to confirm changes.",
            component: <AudioTranslationEditForm {...props} row={audioTranslations.selected?.[0]} callback={(confirm, options) => handleUpdate(confirm, options)} />
        });
    };

    const showApproveDialog = () => {
        props.showDialog({
            title: "Approve audio translation(s)",
            message: `Are you sure you want to approve the ${audioTranslations.selected.length} selected audio translation(s)?`,
            actionTitle: "Approve",
            actionColor: "success",
            callback: (confirm) => handleApprove(confirm)
        });
    };

    const showRetryDialog = () => {
        props.showDialog({
            title: "Retry audio translation(s)",
            message: `Are you sure you want to retry the ${audioTranslations.selected.length} selected audio translation(s)?`,
            actionTitle: "Retry",
            actionColor: "success",
            callback: (confirm) => handleRetry(confirm)
        });
    };

    const handleCreate = (confirm, options = {}) => {
        props.setOpenDialog(false); // Manually close the dialog.
        if (!confirm) return;

        const { row } = options;
        const body = createUploadObject(row, ["form", "_*"]);

        props.showSnackbar({ color: "info", message: "Creating audio translation...", duration: 5000 });
        createAudioTranslation(props, { body });
    };

    const handleDelete = (confirm) => {
        if (!confirm) return;

        const ids = audioTranslations.selected.map((request) => request.id);
        deleteAudioTranslations(props, { body: { ids }, ids });
    };

    const handleUpdate = (confirm, options = {}) => {
        props.setOpenDialog(false); // Manually close the dialog.
        if (!confirm) return;

        const { row } = options;
        const body = createUploadObject(row, ["form", "_*"]);

        updateAudioTranslation(props, { body, id: row.id }); // Using create endpoint for updates as well
    };

    const handleApprove = (confirm) => {
        if (!confirm) return;

        const ids = audioTranslations.selected.map((request) => request.id);
        approveAudioTranslations(props, { body: { ids } });
    };

    const handleRetry = (confirm) => {
        if (!confirm) return;

        const ids = audioTranslations.selected.map((request) => request.id);
        retryAudioTranslations(props, { body: { ids } });
    };

    return (
        <PaperFragment
            className="tour-audio-translations-2 tour-audio-translations-3"
            title={
                <StackedHeader
                    {...props}
                    title="Audio translations"
                    subtitle="View and manage audio translations."
                    icons={[
                        {
                            icon: <DeleteIcon />,
                            tooltip: "Delete selected audio translations",
                            disabled: audioTranslations.selected.length === 0 || audioTranslations.selected.some((r) => ["IN_PROGRESS"].includes(r.status)),
                            onClick: showDeleteDialog
                        },
                        {
                            icon: <EditIcon />,
                            tooltip: "Edit selected audio translation",
                            onClick: showUpdateDialog,
                            disabled: audioTranslations.selected.length !== 1
                        },
                        {
                            icon: <CheckIcon />,
                            tooltip: "Approve selected audio translations",
                            onClick: showApproveDialog,
                            disabled: audioTranslations.selected.length === 0 || !audioTranslations.selected.every((r) => r.status === "AWAITING_APPROVAL")
                        },
                        {
                            icon: <ReplayIcon />,
                            tooltip: "Retry selected audio translations",
                            onClick: showRetryDialog,
                            disabled: audioTranslations.selected.length === 0 || !audioTranslations.selected.every((r) => r.status === "ERROR")
                        }
                    ]}
                    buttons={[
                        {
                            icon: <AddIcon />,
                            title: "New audio translation",
                            onClick: showCreateDialog
                        }
                    ]}
                />
            }
            value={
                <DataGridTable
                    {...props}
                    {...defaultTableOptions}
                    rowHeight={60}
                    states={audioTranslations}
                    columns={columns(props)}
                    query={(options) => queryAudioTranslations(props, options)}
                    empty={getLanguage().NO_AUDIO_TRANSLATIONS_FOUND}
                    refresh={10}
                />
            }
        />
    );
}
